import { type FormEvent, useEffect, useState } from "react";

import { ApiError, fetchVersion } from "../lib/api";
import { setToken } from "../lib/auth";
import { oauthAvailable, startSsoLogin } from "../lib/oauth";

interface Props {
  onConnected: () => void;
}

export default function ConnectRoute({ onConnected }: Props) {
  const [token, setTokenInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [version, setVersion] = useState<string | null>(null);
  const [ssoAvailable, setSsoAvailable] = useState(false);

  useEffect(() => {
    fetchVersion()
      .then((v) => setVersion(v.version))
      .catch(() => setVersion(null));
    oauthAvailable().then(setSsoAvailable);
  }, []);

  async function sso() {
    setError(null);
    try {
      await startSsoLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token.trim()) {
      setError("Bitte einen Bearer-Token eingeben.");
      return;
    }
    setSubmitting(true);
    setError(null);
    setToken(token.trim());
    // Touch a scope-gated endpoint to validate the token before persisting.
    try {
      const r = await fetch("/core/v1/cost/summary?from_date=2026-01-01&to_date=2026-01-01&group_by=pack", {
        headers: { Authorization: `Bearer ${token.trim()}`, Accept: "application/json" },
      });
      if (r.status === 401) {
        throw new ApiError(401, "Token ungültig");
      }
      onConnected();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto mt-24 p-6 bg-kw-bg-elevated border border-kw-border-subtle rounded">
      <h1 className="text-xl font-medium mb-2">kw://cost</h1>
      <p className="text-sm text-kw-text-muted mb-6">
        Cost-Analyse für kontextwerk — verbinde dich mit einem Bearer-Token.
        Sichtbar werden Kosten gemäß Token-Scope (own-subject, Realm, Org oder
        platform-weit).
      </p>
      {ssoAvailable && (
        <div className="mb-4 space-y-2">
          <button
            onClick={sso}
            className="w-full bg-kw-l3 text-white py-2 rounded font-medium hover:opacity-90"
          >
            Mit SSO anmelden
          </button>
          <div className="text-xs text-kw-text-muted text-center">
            oder mit Bearer-Token (Operator/Solo) ↓
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block text-sm">
          <span className="text-kw-text-muted">Bearer-Token</span>
          <input
            type="password"
            value={token}
            onChange={(e) => setTokenInput(e.target.value)}
            className="mt-1 block w-full bg-kw-bg-deep border border-kw-border-subtle rounded px-3 py-2 font-mono text-sm"
            placeholder="eyJhbGciOiJFZERTQSIs…"
            autoFocus
          />
        </label>
        {error ? <p className="text-sm text-red-400">{error}</p> : null}
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-kw-l3 text-white py-2 rounded font-medium disabled:opacity-50"
        >
          {submitting ? "Verbinde…" : "Verbinden"}
        </button>
      </form>
      {version ? (
        <div className="mt-6 text-xs text-kw-text-faint text-center">
          kw-core {version}
        </div>
      ) : null}
    </div>
  );
}
